import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { AgentTask } from '@/stores/agentStore';

interface AgentActionEditorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (task: AgentTask) => void;
  existingTask?: AgentTask;
}

const AgentActionEditor: React.FC<AgentActionEditorProps> = ({
  open,
  onOpenChange,
  onSave,
  existingTask
}) => {
  const [activeTab, setActiveTab] = useState('basic');
  const [taskType, setTaskType] = useState(existingTask?.taskType || 'manual');
  const [scheduleType, setScheduleType] = useState(existingTask?.scheduleType || 'once');
  const [instructions, setInstructions] = useState(existingTask?.instructions || '');
  const [targetNodeIds, setTargetNodeIds] = useState<string[]>(
    existingTask?.targetNodeIds || []
  );
  const [frequency, setFrequency] = useState(existingTask?.frequency || '');
  const [maxRuns, setMaxRuns] = useState(existingTask?.maxRuns || 1);
  const [startDate, setStartDate] = useState(existingTask?.startDate || '');
  const [endDate, setEndDate] = useState(existingTask?.endDate || '');
  
  // Handle save
  const handleSave = () => {
    if (!instructions.trim()) {
      alert('Task instructions are required');
      return;
    }
    
    const task: AgentTask = existingTask ? {
      ...existingTask,
      taskType,
      scheduleType,
      instructions,
      targetNodeIds: targetNodeIds.length > 0 ? targetNodeIds : [],
      frequency: scheduleType === 'recurring' ? frequency : undefined,
      maxRuns: scheduleType === 'recurring' ? maxRuns : undefined,
      startDate: scheduleType !== 'manual' ? startDate : undefined,
      endDate: scheduleType !== 'manual' ? endDate : undefined,
      updatedAt: new Date().toISOString()
    } : {
      id: `task-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      agentId: '', // Will be set by the parent component
      taskType,
      scheduleType: taskType === 'manual' ? undefined : scheduleType,
      instructions,
      targetNodeIds: targetNodeIds.length > 0 ? targetNodeIds : [],
      status: 'pending',
      currentRuns: 0,
      frequency: scheduleType === 'recurring' ? frequency : undefined,
      maxRuns: scheduleType === 'recurring' ? maxRuns : undefined,
      startDate: scheduleType !== 'manual' ? startDate : undefined,
      endDate: scheduleType !== 'manual' ? endDate : undefined
    };
    
    onSave(task);
    onOpenChange(false);
  };
  // Placeholder variables for task instructions
  const instructionPlaceholders = [
    { name: '{{nodeIds}}', description: 'IDs of the nodes being processed' },
    { name: '{{content}}', description: 'Content of the selected nodes' },
    { name: '{{agentName}}', description: 'Name of the agent' },
    { name: '{{projectName}}', description: 'Name of the current project' },
    { name: '{{userName}}', description: 'Name of the current user' },
    { name: '{{nodeTypes}}', description: 'Types of nodes being processed' },
    { name: '{{timestamp}}', description: 'Current timestamp' },
    { name: '{{context}}', description: 'Current project context' },
    { name: '{{previousResults}}', description: 'Results from previous tasks' }
  ];
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {existingTask ? 'Edit Agent Task' : 'Create New Agent Task'}
          </DialogTitle>
        </DialogHeader>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid grid-cols-3 mb-4">
            <TabsTrigger value="basic">Basic Settings</TabsTrigger>
            <TabsTrigger value="schedule">Schedule & Timing</TabsTrigger>
            <TabsTrigger value="target">Target Nodes</TabsTrigger>
          </TabsList>
          
          {/* Basic Settings Tab */}
          <TabsContent value="basic" className="space-y-4">
            <div className="space-y-4">
              <div>
                <Label htmlFor="task-type">Task Type</Label>
                <Select
                  value={taskType}
                  onValueChange={(value) => setTaskType(value)}
                >
                  <SelectTrigger id="task-type">
                    <SelectValue placeholder="Select task type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Manual (User-triggered)</SelectItem>
                    <SelectItem value="automatic">Automatic (Event-triggered)</SelectItem>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="instructions">Task Instructions</Label>
                <div className="mb-2">
                  <p className="text-xs text-gray-600">
                    Define what the agent should do when this task is executed.
                    Use placeholders to dynamically insert content.
                  </p>
                </div>
                <Textarea
                  id="instructions"
                  placeholder="Enter detailed instructions for this task"
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value)}
                  rows={6}
                  className="font-mono text-sm"
                />
                <div className="mt-2">
                  <p className="text-sm text-gray-500 mb-1">Available placeholders:</p>
                  <div className="flex flex-wrap gap-2">
                    {instructionPlaceholders.map((placeholder) => (
                      <div
                        key={placeholder.name}
                        className="text-xs bg-gray-100 px-2 py-1 rounded cursor-pointer hover:bg-gray-200"
                        onClick={() => setInstructions(instructions + ' ' + placeholder.name)}
                        title={placeholder.description}
                      >
                        {placeholder.name}
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <h4 className="text-sm font-medium text-blue-800 mb-1">Task Instructions Tips</h4>
                  <ul className="text-xs text-blue-700 list-disc pl-4 space-y-1">
                    <li>Be specific about what you want the agent to analyze or create</li>
                    <li>Include instructions for formatting the response</li>
                    <li>Use placeholders to reference node content and context</li>
                    <li>Consider including examples of desired output</li>
                  </ul>
                </div>
              </div>
            </div>
          </TabsContent>
          
          {/* Schedule & Timing Tab */}
          <TabsContent value="schedule" className="space-y-4">
            <div className="space-y-4">
              {taskType !== 'manual' && (
                <>
                  <div>
                    <Label htmlFor="schedule-type">Schedule Type</Label>
                    <Select
                      value={scheduleType}
                      onValueChange={(value) => setScheduleType(value)}
                    >
                      <SelectTrigger id="schedule-type">
                        <SelectValue placeholder="Select schedule type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="once">Run Once</SelectItem>
                        <SelectItem value="recurring">Recurring</SelectItem>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="hourly">Hourly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {scheduleType === 'recurring' && (
                    <>
                      <div>
                        <Label htmlFor="frequency">Frequency</Label>
                        <Input
                          id="frequency"
                          placeholder="e.g., every 2 hours, every 30 minutes"
                          value={frequency}
                          onChange={(e) => setFrequency(e.target.value)}
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="max-runs">Maximum Runs (Optional)</Label>
                        <Input
                          id="max-runs"
                          type="number"
                          placeholder="Leave empty for unlimited"
                          value={maxRuns || ''}
                          onChange={(e) => setMaxRuns(parseInt(e.target.value) || undefined)}
                        />
                      </div>
                    </>
                  )}
                  
                  <div>
                    <Label htmlFor="start-date">Start Date/Time</Label>
                    <Input
                      id="start-date"
                      type="datetime-local"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="end-date">End Date/Time (Optional)</Label>
                    <Input
                      id="end-date"
                      type="datetime-local"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                </>
              )}
              
              {taskType === 'manual' && (
                <div className="text-center text-gray-500 py-8">
                  <p>Manual tasks don't require scheduling.</p>
                  <p>They can be triggered on-demand by users.</p>
                </div>
              )}
            </div>
          </TabsContent>
          
          {/* Target Nodes Tab */}
          <TabsContent value="target" className="space-y-4">
            <div className="mb-3">
              <p className="text-sm text-gray-600">
                Configure which nodes this task should target when executed.
              </p>
            </div>
            
            <div className="space-y-4">
              <div>
                <Label>Target Node IDs (Optional)</Label>
                <p className="text-xs text-gray-600 mb-2">
                  Specify exact node IDs to target. Leave empty to target all monitored nodes.
                </p>
                <Textarea
                  placeholder="Enter node IDs separated by commas or newlines"
                  value={targetNodeIds.join('\n')}
                  onChange={(e) => setTargetNodeIds(
                    e.target.value.split(/[,\n]/)
                      .map(id => id.trim())
                      .filter(id => id.length > 0)
                  )}
                  rows={4}
                  className="font-mono text-sm"
                />
              </div>
              
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <h4 className="text-sm font-medium text-yellow-800 mb-1">Target Selection Info</h4>
                <ul className="text-xs text-yellow-700 list-disc pl-4 space-y-1">
                  <li>If no specific node IDs are provided, the task will target all nodes in the agent's observation list</li>
                  <li>You can use node IDs to target specific nodes regardless of monitoring settings</li>
                  <li>The agent must have appropriate permissions to access the target nodes</li>
                </ul>
              </div>
            </div>
          </TabsContent>
        </Tabs>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            {existingTask ? 'Save Changes' : 'Create Task'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AgentActionEditor;